<template>
  <div class="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 flex items-center justify-center p-4">
    <!-- Particules animées -->
    <div
        v-for="particle in particles"
        :key="particle.id"
        class="absolute w-2 h-2 bg-red-400/20 rounded-full animate-float"
        :style="{
          left: particle.x + '%',
          top: particle.y + '%',
          animationDelay: particle.delay + 's',
          animationDuration: particle.duration + 's'
        }"
    />

    <!-- Formes géométriques décoratives -->
    <div class="absolute top-20 right-20 w-32 h-32 bg-red-500/10 rounded-full animate-pulse" />
    <div class="absolute bottom-32 left-16 w-24 h-24 bg-orange-500/10 rounded-lg rotate-45 animate-bounce-slow" />
    <div class="absolute top-1/3 left-10 w-16 h-16 bg-amber-500/10 rounded-full animate-ping-slow" />

    <!-- Contenu principal -->
    <div class="relative z-10 text-center max-w-4xl mx-auto">
      <!-- Icône avec animation -->
      <div class="mb-8 flex justify-center">
        <div class="relative group">
          <div class="w-24 h-24 bg-gradient-to-tr from-red-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-2xl transform transition-all duration-700 group-hover:scale-110 group-hover:rotate-3">
            <div class="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Icon name="pi:shield" class="text-white text-3xl" />
            </div>
          </div>
          <div class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full animate-bounce flex items-center justify-center">
            <Icon name="pi:times" class="text-white text-xs" />
          </div>
        </div>
      </div>

      <!-- Titre principal -->
      <h1 class="text-4xl md:text-5xl font-bold bg-gradient-to-r from-red-600 via-orange-600 to-amber-600 bg-clip-text text-transparent mb-4">
        <span class="animate-fade-in-up">{{ t('errors.unauthorized.title') }}</span>
      </h1>

      <!-- Message personnalisé -->
      <div class="mb-8 animate-fade-in-up animation-delay-300">
        <p class="text-lg md:text-xl text-gray-700 mb-4">
          {{ t('errors.unauthorized.message') }}
        </p>
        <p class="text-md text-gray-600">
          {{ t('errors.unauthorized.requestedPath', { path: requestedPath }) }}
        </p>
      </div>

      <!-- Permissions requises -->
      <Card v-if="requiredPermissions.length > 0" class="mb-6 bg-red-50/70 backdrop-blur-sm border border-red-200/50 shadow-xl animate-fade-in-up animation-delay-600">
        <template #content>
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="pi:lock" class="text-red-600 text-xl" />
            </div>
            <div class="text-left flex-1">
              <h3 class="font-semibold text-red-800 mb-3">
                {{ t('errors.unauthorized.requiredPermissions') }}
              </h3>
              <div class="flex flex-wrap gap-2">
                <Tag
                    v-for="permission in requiredPermissions"
                    :key="permission"
                    :value="formatPermission(permission)"
                    severity="danger"
                    class="px-3 py-1"
                />
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- Permissions actuelles de l'utilisateur -->
      <Card v-if="userPermissions.length > 0" class="mb-8 bg-green-50/70 backdrop-blur-sm border border-green-200/50 shadow-xl animate-fade-in-up animation-delay-900">
        <template #content>
          <h3 class="font-semibold text-green-800 mb-4 flex items-center gap-2">
            <Icon name="pi:check-circle" class="text-green-600" />
            {{ t('errors.unauthorized.yourPermissions') }}
          </h3>
          <div class="flex flex-wrap gap-2 justify-center">
            <Tag
                v-for="permission in userPermissions"
                :key="permission"
                :value="$t(`permissions_desc.${permission}`)"
                severity="success"
                class="px-3 py-1"
            />
          </div>
          <p class="text-sm text-gray-600 mt-4">
            {{ t('errors.unauthorized.permissionsDescription') }}
          </p>
        </template>
      </Card>

      <!-- Services accessibles -->
      <Card v-if="accessibleServices.length > 0" class="mb-8 bg-blue-50/70 backdrop-blur-sm border border-blue-200/50 shadow-xl animate-fade-in-up animation-delay-1200">
        <template #content>
          <h3 class="font-semibold text-blue-800 mb-4 flex items-center gap-2">
            <Icon name="pi:compass" class="text-blue-600" />
            {{ t('errors.unauthorized.accessibleServices') }}
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
            <div
                v-for="service in accessibleServices"
                :key="service.path"
                @click="navigateTo(service.path)"
                class="p-3 bg-white/80 rounded-lg hover:bg-blue-100/80 transition-colors cursor-pointer group"
            >
              <div class="flex items-center gap-3">
                <Icon :name="service.icon" class="text-blue-600 text-xl group-hover:scale-110 transition-transform" />
                <div>
                  <p class="font-medium text-gray-800">{{ service.name }}</p>
                  <p class="text-xs text-gray-600">{{ service.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- Boutons d'action -->
      <div class="flex flex-wrap gap-4 justify-center animate-fade-in-up animation-delay-1500">
        <Button
            :label="t('errors.unauthorized.backHome')"
            icon="pi pi-home"
            class="p-button-lg bg-gradient-to-r from-orange-500 to-amber-600 border-0 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            @click="goHome"
        />
<!--        <Button-->
<!--            v-if="canRequestAccess"-->
<!--            :label="t('errors.unauthorized.requestAccess')"-->
<!--            icon="pi pi-envelope"-->
<!--            class="p-button-lg p-button-outlined border-2 border-orange-500 text-orange-600 hover:bg-orange-50 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"-->
<!--            @click="requestAccess"-->
<!--        />-->
      </div>

      <!-- Message d'information -->
      <div class="mt-8 p-4 bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl inline-block animate-fade-in-up animation-delay-1800">
        <p class="text-orange-800 font-medium flex items-center gap-2">
          <Icon name="pi:info-circle" class="text-orange-600" />
          {{ t('errors.unauthorized.contactAdmin') }}
        </p>
      </div>
    </div>

    <!-- Toast pour les notifications -->
<!--    <Toast position="top-right" />-->
  </div>
</template>

<script setup>
import { useAuthStore } from '~/stores/auth'
// import { useToast } from 'primevue/usetoast'

// Props avec valeurs par défaut
const props = defineProps({
  requiredPermissions: {
    type: Array,
    default: () => []
  },
  requestedPath: {
    type: String,
    default: '/unknown'
  },
  canRequestAccess: {
    type: Boolean,
    default: false
  }
})

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

// Récupérer les permissions de l'utilisateur depuis le store
const userPermissions = computed(() => {
  return authStore.user?.role?.permissions || []
})

// Générer la liste des services accessibles selon les permissions
const accessibleServices = computed(() => {
  const services = []
  const perms = userPermissions.value

  // Dashboard
  if (authStore.isAuthenticated) {
    // Toujours accessible
    services.push({
      name: t('services.dashboard'),
      path: '/admin',
      icon: 'heroicons:chart-line',
      description: t('services.dashboardDesc')
    })
    services.push({
      name: t('services.profile'),
      path: '/profile',
      icon: 'heroicons:user',
      description: t('services.profileDesc')
    })
  }

  // Projets
  if (perms.includes('project')) {
    services.push({
      name: t('services.projects'),
      path: '/projects',
      icon: 'heroicons:folder',
      description: t('services.projectsDesc')
    })
  }

  // Utilisateurs
  if (perms.includes('user')) {
    services.push({
      name: t('services.users'),
      path: '/admin/users',
      icon: 'heroicons:users',
      description: t('services.usersDesc')
    })
  }

  // Rôles
  if (perms.includes('role')) {
    services.push({
      name: t('services.roles'),
      path: '/admin/roles',
      icon: 'heroicons:shield',
      description: t('services.rolesDesc')
    })
  }




  return services
})


// Génération des particules
const particles = ref(
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2
    }))
)

// Actions
const goHome = () => {
  navigateTo('/')
}

// const requestAccess = () => {
//   toast.add({
//     severity: 'info',
//     summary: t('errors.unauthorized.requestSent'),
//     detail: t('errors.unauthorized.requestSentDesc'),
//     life: 5000
//   })
// }

// Meta tags
useHead({
  title: t('errors.unauthorized.metaTitle'),
  meta: [
    {
      name: 'description',
      content: t('errors.unauthorized.metaDescription')
    },
    {
      name: 'robots',
      content: 'noindex, nofollow'
    }
  ]
})
</script>

<style scoped>
@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(180deg); }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out forwards;
  opacity: 0;
}

.animation-delay-300 { animation-delay: 0.3s; }
.animation-delay-600 { animation-delay: 0.6s; }
.animation-delay-900 { animation-delay: 0.9s; }
.animation-delay-1200 { animation-delay: 1.2s; }
.animation-delay-1500 { animation-delay: 1.5s; }
.animation-delay-1800 { animation-delay: 1.8s; }

.animate-bounce-slow { animation: bounce 3s infinite; }
.animate-ping-slow {
  animation: ping 3s cubic-bezier(0, 0, 0.2, 1) infinite;
  animation-delay: 1s;
}

:deep(.p-card) { border-radius: 1rem; }
:deep(.p-button) { border-radius: 0.75rem; }
:deep(.p-tag) { border-radius: 0.5rem; }
</style>